/**
 * Метки (user_tags) — CRUD inline для группировки юзеров под рассылки.
 */
import { useEffect, useState } from "react";
import {
  Card, Table, Tag, Button, Modal, Input, Space, Typography, ColorPicker,
  message, Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { API_URL } from "../../dataProvider";

interface TagRow {
  id: number;
  name: string;
  color: string;
  description: string | null;
  created_at: string | null;
  users_count: number;
}

export const TagsPage = () => {
  const [rows, setRows] = useState<TagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<TagRow> | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/tags`, { credentials: "include" });
      if (r.ok) setRows(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const name = (editing.name || "").trim();
    if (!name) { message.warning("Название обязательно"); return; }
    const url = editing.id
      ? `${API_URL}/admin/tags/${editing.id}`
      : `${API_URL}/admin/tags`;
    const method = editing.id ? "PATCH" : "POST";
    const r = await fetch(url, {
      method, credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name, color: editing.color || "#888888",
        description: editing.description ?? null,
      }),
    });
    if (!r.ok) {
      const t = await r.text();
      message.error(`Ошибка: ${t.slice(0, 200)}`);
      return;
    }
    setEditing(null); load();
    message.success(editing.id ? "Метка обновлена" : "Метка создана");
  };

  const del = async (id: number) => {
    const r = await fetch(`${API_URL}/admin/tags/${id}`, {
      method: "DELETE", credentials: "include",
    });
    if (r.ok) { message.success("Удалено"); load(); }
    else message.error("Не удалось удалить");
  };

  return (
    <Card title="Метки юзеров" extra={
      <Button type="primary" icon={<PlusOutlined />}
              onClick={() => setEditing({ color: "#1677ff" })}>
        Новая метка
      </Button>
    }>
      <Typography.Paragraph type="secondary">
        Метки группируют юзеров. Используются для сегментирования рассылок
        («отправить всем юзерам с меткой X»). Юзеру можно назначить несколько меток.
      </Typography.Paragraph>
      <Table dataSource={rows} loading={loading} rowKey="id" pagination={false}>
        <Table.Column dataIndex="name" title="Метка" width={220}
          render={(name: string, r: TagRow) => (
            <Tag color={r.color} style={{ fontSize: 13, padding: "2px 10px" }}>
              {name}
            </Tag>
          )} />
        <Table.Column dataIndex="description" title="Описание"
          render={(v: string) => v || <Typography.Text type="secondary">—</Typography.Text>} />
        <Table.Column<TagRow> title="Юзеров" width={140}
          render={(_, r: any) => {
            const all = (r.users_count_all ?? r.users_count) || 0;
            const valid = r.users_count;
            return (
              <Typography.Text strong>
                {valid}
                {all > valid && (
                  <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                    /{all}
                  </Typography.Text>
                )}
              </Typography.Text>
            );
          }} />
        <Table.Column<TagRow> title="" width={120}
          render={(_, r) => (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />}
                onClick={() => setEditing(r)} />
              <Popconfirm title="Удалить метку?" onConfirm={() => del(r.id)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          )} />
      </Table>

      <Modal open={editing !== null}
             title={editing?.id ? "Редактировать метку" : "Создать метку"}
             onCancel={() => setEditing(null)} onOk={save}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Input placeholder="Название" value={editing?.name || ""}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          <Input placeholder="Описание (необязательно)" value={editing?.description || ""}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          <Space>
            <span>Цвет:</span>
            <ColorPicker value={editing?.color || "#1677ff"}
              onChange={(c) => setEditing({ ...editing, color: c.toHexString() })} />
          </Space>
        </Space>
      </Modal>
    </Card>
  );
};
